import React, { useCallback, useState } from 'react';
import { Box, Card, CardContent, Container, Divider, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import PersistentServerSideDataGrid from 'common/PersistentServerSideDataGrid';

// Mock data generators
const generateMockRows = (count = 200) => {
  const statuses = ['Active', 'Pending', 'Completed', 'Cancelled', 'In Transit'];
  const products = ['Crude Oil', 'Gasoline', 'Diesel', 'Jet Fuel', 'Natural Gas'];
  const locations = ['Houston TX', 'Cushing OK', 'New York NY', 'Los Angeles CA', 'Chicago IL'];
  const owners = ['Company A', 'Company B', 'Company C', 'Company D', 'Company E'];

  return Array.from({ length: count }, (_, index) => ({
    batchId: `BATCH-${String(index + 1).padStart(5, '0')}`,
    createdAt: new Date(2026, 0, Math.floor(Math.random() * 90) + 1),
    date: new Date(2026, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1),
    id: index + 1,
    location: locations[Math.floor(Math.random() * locations.length)],
    owner: owners[Math.floor(Math.random() * owners.length)],
    price: parseFloat((Math.random() * 100 + 50).toFixed(2)),
    product: products[Math.floor(Math.random() * products.length)],
    status: statuses[Math.floor(Math.random() * statuses.length)],
    volume: Math.floor(Math.random() * 10000) + 1000,
  }));
};

// Full dataset (simulating backend data)
const fullDataset = generateMockRows(200);

// Base column definitions
const getBaseColumns = () => [
  {
    field: 'batchId',
    headerName: 'Batch ID',
    width: 150,
  },
  {
    field: 'status',
    filterable: true,
    headerName: 'Status',
    width: 140,
  },
  {
    cellClassName: 'css-heap-redact',
    field: 'owner',
    headerName: 'Owner',
    width: 150,
  },
  {
    field: 'product',
    filterable: true,
    headerName: 'Product',
    width: 150,
  },
  {
    field: 'location',
    filterable: true,
    headerName: 'Location',
    width: 150,
  },
  {
    field: 'volume',
    headerName: 'Volume (bbls)',
    sortable: true,
    type: 'number',
    valueFormatter: params => params.value?.toLocaleString(),
    width: 150,
  },
  {
    field: 'price',
    headerName: 'Price ($)',
    sortable: true,
    type: 'number',
    valueFormatter: params => `$${params.value?.toFixed(2)}`,
    width: 120,
  },
  {
    field: 'date',
    headerName: 'Available Date',
    sortable: true,
    type: 'date',
    valueFormatter: params => format(params.value, 'MM/dd/yyyy'),
    width: 140,
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    sortable: true,
    type: 'dateTime',
    valueFormatter: params => format(params.value, 'MM/dd/yyyy HH:mm'),
    width: 160,
  },
];

const PersistentServerSideDataGridPreview = () => {
  // State for Example 1: Basic server-side grid
  const [rows1, setRows1] = useState([]);
  const [loading1, setLoading1] = useState(false);
  const [totalRows1, setTotalRows1] = useState(0);

  // State for Example 2: With filtering and sorting
  const [rows2, setRows2] = useState([]);
  const [loading2, setLoading2] = useState(false);
  const [totalRows2, setTotalRows2] = useState(0);

  // State for Example 3: With selection
  const [rows3, setRows3] = useState([]);
  const [loading3, setLoading3] = useState(false);
  const [totalRows3, setTotalRows3] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);

  // Simulate server-side data fetching
  const fetchData = useCallback((paginationModel, sortModel = [], filterModel = { items: [] }) => {
    return new Promise(resolve => {
      setTimeout(() => {
        let filteredData = [...fullDataset];

        // Apply filters
        filterModel.items.forEach(filter => {
          if (filter.value) {
            filteredData = filteredData.filter(row => {
              const cellValue = String(row[filter.field]).toLowerCase();
              const filterValue = String(filter.value).toLowerCase();
              switch (filter.operator) {
                case 'contains':
                  return cellValue.includes(filterValue);
                case 'equals':
                  return cellValue === filterValue;
                case 'startsWith':
                  return cellValue.startsWith(filterValue);
                case 'endsWith':
                  return cellValue.endsWith(filterValue);
                default:
                  return cellValue.includes(filterValue);
              }
            });
          }
        });

        // Apply sorting
        if (sortModel.length > 0) {
          const { field, sort } = sortModel[0];
          filteredData.sort((a, b) => {
            const aValue = a[field];
            const bValue = b[field];
            if (aValue < bValue) return sort === 'asc' ? -1 : 1;
            if (aValue > bValue) return sort === 'asc' ? 1 : -1;
            return 0;
          });
        }

        const total = filteredData.length;
        const start = paginationModel.page * paginationModel.pageSize;
        const end = start + paginationModel.pageSize;
        const paginatedData = filteredData.slice(start, end);

        resolve({
          rows: paginatedData,
          total,
        });
      }, 500); // Simulate network delay
    });
  }, []);

  // Handler for Example 1
  const handlePaginationChange1 = useCallback(
    paginationModel => {
      setLoading1(true);
      fetchData(paginationModel).then(({ rows, total }) => {
        setRows1(rows);
        setTotalRows1(total);
        setLoading1(false);
      });
    },
    [fetchData]
  );

  // Handler for Example 2
  const handleDataChange2 = useCallback(
    (paginationModel, sortModel, filterModel) => {
      setLoading2(true);
      fetchData(paginationModel, sortModel, filterModel).then(({ rows, total }) => {
        setRows2(rows);
        setTotalRows2(total);
        setLoading2(false);
      });
    },
    [fetchData]
  );

  // Handler for Example 3
  const handleDataChange3 = useCallback(
    (paginationModel, sortModel, filterModel) => {
      setLoading3(true);
      fetchData(paginationModel, sortModel, filterModel).then(({ rows, total }) => {
        setRows3(rows);
        setTotalRows3(total);
        setLoading3(false);
      });
    },
    [fetchData]
  );

  // Export handler - fetch all rows for export
  const handleExportAllRows = useCallback(async () => {
    // Simulate fetching all rows from server
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(fullDataset);
      }, 800);
    });
  }, []);

  // Initialize data on mount
  React.useEffect(() => {
    handlePaginationChange1({ page: 0, pageSize: 10 });
    handleDataChange2({ page: 0, pageSize: 10 }, [], { items: [] });
    handleDataChange3({ page: 0, pageSize: 10 }, [], { items: [] });
  }, [handlePaginationChange1, handleDataChange2, handleDataChange3]);

  return (
    <Container maxWidth="xl" sx={{ py: 8 }}>
      <Typography gutterBottom variant="h3">
        PersistentServerSideDataGrid Preview
      </Typography>
      <Typography
        color="text.secondary"
        sx={{
          marginBottom: '16px',
        }}
        variant="body1"
      >
        This page showcases the PersistentServerSideDataGrid component with server-side pagination,
        sorting, and filtering. The component is designed for large datasets where data is fetched
        from an API in pages rather than loading all data at once.
      </Typography>
      <Divider sx={{ my: 3 }} />
      <Stack spacing={4}>
        {/* 1. Basic Server-Side Grid */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              1. Basic Server-Side Pagination
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Demonstrates basic server-side pagination. Total dataset: {totalRows1 || 200} rows.
              Data is fetched from the &ldquo;server&rdquo; when you change pages. Notice the
              loading indicator and simulated network delay.
            </Typography>
            <Box sx={{ height: 500, width: '100%' }}>
              <PersistentServerSideDataGrid
                columns={getBaseColumns()}
                defaultPageSize={10}
                id="server-side-preview-basic"
                loading={loading1}
                onPaginationChange={handlePaginationChange1}
                pageSizeOptions={[10, 25, 50]}
                rows={rows1}
                totalRows={totalRows1}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 2. With Sorting and Filtering */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              2. Server-Side Sorting and Filtering
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Try sorting columns and applying filters. The grid sends the sort/filter parameters to
              the &ldquo;server&rdquo; and fetches matching data. Current results: {totalRows2}{' '}
              rows.
            </Typography>
            <Box sx={{ height: 500, width: '100%' }}>
              <PersistentServerSideDataGrid
                columns={getBaseColumns()}
                defaultPageSize={10}
                id="server-side-preview-filtering"
                loading={loading2}
                onFilterChange={filterModel => {
                  handleDataChange2({ page: 0, pageSize: 10 }, [], filterModel);
                }}
                onPaginationChange={paginationModel => {
                  handleDataChange2(paginationModel, [], { items: [] });
                }}
                onSortChange={sortModel => {
                  handleDataChange2({ page: 0, pageSize: 10 }, sortModel, { items: [] });
                }}
                pageSizeOptions={[10, 25, 50]}
                rows={rows2}
                totalRows={totalRows2}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 3. With Row Selection */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              3. With Checkbox Selection
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Server-side grid with row selection enabled. Selected rows: {selectedRows.length}
            </Typography>
            <Box sx={{ height: 500, width: '100%' }}>
              <PersistentServerSideDataGrid
                checkboxSelection
                columns={getBaseColumns()}
                defaultPageSize={10}
                id="server-side-preview-selection"
                loading={loading3}
                onFilterChange={filterModel => {
                  handleDataChange3({ page: 0, pageSize: 10 }, [], filterModel);
                }}
                onPaginationChange={paginationModel => {
                  handleDataChange3(paginationModel, [], { items: [] });
                }}
                onSelectionChange={newSelection => setSelectedRows(newSelection)}
                onSortChange={sortModel => {
                  handleDataChange3({ page: 0, pageSize: 10 }, sortModel, { items: [] });
                }}
                pageSizeOptions={[10, 25, 50]}
                rows={rows3}
                totalRows={totalRows3}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 4. With Custom Export */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              4. Server-Side Grid with Full Export
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              When exporting, the grid fetches all {fullDataset.length} rows from the server (not
              just the current page) and exports them to CSV. Click the Export button to try it.
            </Typography>
            <Box sx={{ height: 500, width: '100%' }}>
              <PersistentServerSideDataGrid
                columns={getBaseColumns()}
                defaultPageSize={10}
                id="server-side-preview-export"
                loading={loading2}
                onExportAllRows={handleExportAllRows}
                onFilterChange={filterModel => {
                  handleDataChange2({ page: 0, pageSize: 10 }, [], filterModel);
                }}
                onPaginationChange={paginationModel => {
                  handleDataChange2(paginationModel, [], { items: [] });
                }}
                onSortChange={sortModel => {
                  handleDataChange2({ page: 0, pageSize: 10 }, sortModel, { items: [] });
                }}
                pageSizeOptions={[10, 25, 50]}
                rows={rows2}
                totalRows={totalRows2}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 5. Loading Overlay */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              5. Loading Overlay State
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Demonstrates the default loading overlay (DataLoadingCard) during server-side data
              fetching. This is the standard loading indicator users see while waiting for data.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: 800, width: '100%' }}>
              <PersistentServerSideDataGrid
                columns={getBaseColumns()}
                defaultPageSize={10}
                id="server-side-preview-loading"
                loading
                pageSizeOptions={[10, 25, 50]}
                rows={[]}
                totalRows={0}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 6. No Rows Overlay */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              6. No Rows Overlay State
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Shows the default empty state overlay (NoAvailableCard) when no data is returned from
              the server or when filters return no results.
            </Typography>
            <Box sx={{ height: 400, width: '100%' }}>
              <PersistentServerSideDataGrid
                columns={getBaseColumns()}
                defaultPageSize={10}
                id="server-side-preview-no-rows"
                pageSizeOptions={[10, 25, 50]}
                rows={[]}
                totalRows={0}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Summary Card */}
        <Card sx={{ bgcolor: 'info.50' }}>
          <CardContent>
            <Typography gutterBottom variant="h6">
              Implementation Notes
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                • <strong>Server-Side Mode:</strong> Data is fetched page-by-page from the server,
                not loaded all at once
              </Typography>
              <Typography variant="body2">
                • <strong>Performance:</strong> Ideal for large datasets (1000s of rows) where
                loading all data would be slow
              </Typography>
              <Typography variant="body2">
                • <strong>Pagination:</strong> Pass <code>onPaginationChange</code> callback to
                fetch new pages when user navigates
              </Typography>
              <Typography variant="body2">
                • <strong>Sorting:</strong> Pass <code>onSortChange</code> to apply sorting on the
                server side
              </Typography>
              <Typography variant="body2">
                • <strong>Filtering:</strong> Pass <code>onFilterChange</code> to filter data on the
                server side
              </Typography>
              <Typography variant="body2">
                • <strong>Export:</strong> Use <code>onExportAllRows</code> to fetch complete
                dataset for CSV export
              </Typography>
              <Typography variant="body2">
                • <strong>State Management:</strong> Component automatically persists column order,
                visibility, and density to localStorage
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
};

export default PersistentServerSideDataGridPreview;
